/**
 * Eine der beiden Phasentabellen der Kostenseite ("Vor ETD fällig" / "Nach ETD fällig") -
 * 1:1-Portierung von `phaseTable` aus `index.html:13849-13867`. Klassennamen, Reihenfolge und Texte
 * sind woertlich uebernommen.
 *
 * ================= Die einzige Ergaenzung: `aria-label` an der Tabelle =================
 *
 * Das Original hat an `<table class="ov">` keinen zugaenglichen Namen; die Ueberschrift steht als
 * `<h2>`-Geschwister in `.ov-section-head`. Der Komponententest des Task-Briefs greift die Tabellen
 * aber ueber `getByRole("table", { name: /vor etd/i })` - er braucht den Namen also. Gewaehlt ist
 * `aria-label`, nicht `<caption>`:
 * - `<caption>` waere ein SICHTBARES Element und wuerde Bild UND DOM-Signatur veraendern - eine
 *   echte Abweichung von der optischen Paritaet.
 * - `aria-*` erfasst `domSignatur` ausdruecklich NICHT ("Bewusst NICHT erfasst: `id`, `title`,
 *   `aria-*` ... Was sie sichern, sichern die Komponententests", `test/visual/signatur.ts`). Der
 *   Zusatz ist damit optisch und strukturell folgenlos und liegt genau in der Kategorie, fuer die
 *   die Signatur den Komponententest vorsieht.
 * Gemeldet im Task-Bericht, nicht stillschweigend eingebaut.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. Die Sortierung `(a.due || '9999').localeCompare(b.due || '9999')` vergleicht ISO-Datumstexte
 *    per `localeCompare` (nicht `<`), und der Rueckfall fuer eine fehlende Faelligkeit ist die
 *    vierstellige Zeichenkette `'9999'` - kuerzer als jedes ISO-Datum, sortiert aber trotzdem ans
 *    Ende, weil `'9'` groesser ist als jede Jahrhundertziffer. Zeichengleich uebernommen.
 * 2. Das Original sortiert IN PLACE (`rows.sort(...)`) auf dem vom Aufrufer uebergebenen Array.
 *    Hier wird vorher kopiert (`sortiereOffenePosten` in `lib/kostenAuswertung.ts`, seit dem KI-Zugang
 *    (Stufe 1) dort, damit der Server dieselbe Reihenfolge nennt): das Ergebnis ist identisch (der Aufrufer
 *    baut die Listen ohnehin je Rendering frisch ueber `.filter`, und die Summen entstehen
 *    unabhaengig von der Reihenfolge), aber eine Komponente, die ihre Props veraendert, waere in
 *    React ein Fehler mit Fernwirkung. Kein Verhaltensunterschied, deshalb keine Meldung als
 *    Abweichung - nur hier vermerkt.
 * 3. `r.overdue` ist NICHT durchgehend ein Wahrheitswert (siehe `lib/fx.ts`, Eigenheit 3): bei
 *    leerem `due` liefert `p.due && p.due < today` den leeren String. Im Original entscheidet
 *    darueber ein `? :`, hier ebenso - JSX bekommt den Ausdruck nie direkt zu sehen, sodass kein
 *    leerer String als Textknoten im DOM landet.
 */
import { fmtDate, EUR0, NUM } from "../../lib/format.ts";
import type { OpenPaymentRow } from "../../lib/fx.ts";
import { sortiereOffenePosten } from "../../lib/kostenAuswertung.ts";
import { fmtMoney } from "../../lib/money.ts";
import type { Supplier } from "../../lib/types.ts";

export interface PhasenTabelleProps {
  /** Ueberschrift der Sektion - zugleich der zugaengliche Name der Tabelle (siehe Kopfkommentar). */
  titel: string;
  zeilen: readonly OpenPaymentRow[];
  /** `"vor"` oder `"nach"` - Zusatzklasse an `.cost-sum` (index.html:13851). */
  klasse: string;
  lieferanten: readonly Supplier[] | null | undefined;
  onOeffnen: (bestellungId: string) => void;
}

/** index.html:13833 - Summe der €-Naeherungen einer Zeilenmenge. */
function sumEur(zeilen: readonly OpenPaymentRow[]): number {
  return zeilen.reduce((s, r) => s + r.eur, 0);
}

/** index.html:13859 - Anzeigename einer Bestellung in der Tabellenzeile. */
function bestellungsName(order: OpenPaymentRow["order"]): string {
  return ((order.orderNo || order.name || "Bestellung") as string);
}

export function PhasenTabelle({ titel, zeilen, klasse, lieferanten, onOeffnen }: PhasenTabelleProps) {
  const sortiert = sortiereOffenePosten(zeilen);
  return (
    <div className="ov-section">
      <div className="ov-section-head">
        <h2>
          {titel} <span className="cost-count">{NUM.format(zeilen.length)}</span>
        </h2>
        <span className={`cost-sum ${klasse}`}>{EUR0.format(sumEur(zeilen))}</span>
      </div>
      <div className="ov-table-wrap">
        {zeilen.length ? (
          <table className="ov" aria-label={titel}>
            <thead>
              <tr>
                <th>Bestellung</th>
                <th>Rate</th>
                <th>Fällig</th>
                <th className="num">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {sortiert.map((r, i) => (
                <tr
                  // Kein fachlicher Schluessel vorhanden: eine Bestellung kann zwei Raten mit
                  // gleichem Namen und gleicher Faelligkeit haben (`payments[]` fuehrt zwar `id`,
                  // aber `orderOpenPayments` gibt sie nicht heraus). Der Index ist hier
                  // unbedenklich - die Liste wird bei jedem Rendering vollstaendig neu gebaut und
                  // traegt keinen eigenen Zustand.
                  key={i}
                  className={`art-row ${r.overdue ? "cost-overdue" : ""}`}
                  onClick={() => onOeffnen(String(r.order.id))}
                >
                  <td>{bestellungsName(r.order)}</td>
                  <td>{r.label}</td>
                  <td>
                    {r.due ? fmtDate(r.due) : "—"}
                    {/* Das fuehrende Leerzeichen steht im Original vor dem <span> (index.html:13861)
                        und ist als `{" "}` geschrieben, damit es nicht von einer spaeteren
                        Umformatierung der Zeile verschluckt wird. */}
                    {r.overdue ? (
                      <>
                        {" "}
                        <span className="cost-flag">überfällig</span>
                      </>
                    ) : (
                      ""
                    )}
                  </td>
                  <td className="num">
                    {fmtMoney(r.order, r.amt, lieferanten)}
                    {r.approx ? (
                      <>
                        {" "}
                        <span className="cost-approx" title="ohne gesicherten Kurs – Betrag in $ statt €">
                          ≈
                        </span>
                      </>
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-note">Nichts offen.</div>
        )}
      </div>
    </div>
  );
}
